import React, { useState, useEffect } from 'react';
import {
  Popover, List, ListItemButton, Box, Button, Grid, Typography,
  MenuItem, Dialog, IconButton, CircularProgress, Chip
} from "@mui/material";
import CloseIcon        from "@mui/icons-material/Close";
import ArrowBackIcon    from "@mui/icons-material/ArrowBack";
import VisibilityIcon   from "@mui/icons-material/Visibility";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GridViewIcon     from "@mui/icons-material/GridView";
import { useNavigate }  from "react-router-dom";
import { getRamos }     from '../../services/RamosServices';
import { getRubros }    from '../../services/RubrosServices';
import { getSubrubros } from '../../services/SubrubrosServices';

// ─── Helpers de navegación ────────────────────────────────────────────────────
// Siempre construye la URL con IDs: /category?r=3&u=12&su=45
const buildUrl = (ramoId, rubroId, subrubroId) => {
  const params = new URLSearchParams();
  if (ramoId)     params.set('r',  ramoId);
  if (rubroId)    params.set('u',  rubroId);
  if (subrubroId) params.set('su', subrubroId);
  return `/category?${params.toString()}`;
};

/* ── Pill Button ─────────────────────────────────────────────────────────────*/
const PillBtn = ({ children, onClick, variant = "accent", size = "sm", startIcon, sx = {} }) => {
  const variants = {
    accent:  { bg: 'var(--accent)',   hover: 'var(--accent-hover)', color: '#fff' },
    danger:  { bg: 'var(--danger)',   hover: '#dc2626',             color: '#fff' },
    success: { bg: 'var(--success)',  hover: '#059669',             color: '#fff' },
    purple:  { bg: 'var(--purple)',   hover: '#6d28d9',             color: '#fff' },
    outline: { bg: 'transparent',    hover: 'var(--bg-subtle)',    color: 'var(--text-secondary)', border: '1.5px solid var(--border)' },
  };
  const v = variants[variant];
  return (
    <Button onClick={onClick} size="small" startIcon={startIcon}
      sx={{
        backgroundColor: v.bg, color: v.color, fontFamily: 'var(--font-body)',
        fontWeight: 700, fontSize: size === 'sm' ? '0.72rem' : '0.8rem',
        textTransform: 'none', borderRadius: 'var(--radius-full)',
        px: size === 'sm' ? 1.5 : 2, py: 0.5,
        border: v.border || 'none',
        boxShadow: variant === 'outline' ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease',
        '&:hover': { backgroundColor: v.hover, transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
        ...sx,
      }}
    >{children}</Button>
  );
};

/* ── Category Card (mobile) ──────────────────────────────────────────────────*/
const CategoryCard = ({ label, onClick, color = 'var(--accent)' }) => (
  <Box onClick={onClick} sx={{
    p: 2, borderRadius: 'var(--radius-md)', cursor: 'pointer',
    backgroundColor: color, color: '#fff', textAlign: 'center',
    boxShadow: 'var(--shadow-sm)', transition: 'all 0.25s ease',
    '&:hover': { boxShadow: 'var(--shadow-md)', transform: 'translateY(-2px)' },
    '&:active': { transform: 'scale(0.97)' },
  }}>
    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', fontFamily: 'var(--font-heading)' }}>{label}</Typography>
  </Box>
);

/* ── Loading ──────────────────────────────────────────────────────────────────*/
const LoadingDisplay = ({ isMobile }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: isMobile ? '50vh' : 80, width: isMobile ? '100%' : 80, p: 4 }}>
    <Box sx={{ width: 44, height: 44, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress size={22} sx={{ color: 'var(--accent)' }} />
    </Box>
  </Box>
);

/* ── Main ─────────────────────────────────────────────────────────────────────*/
const CategoryPopover = ({
  anchorEl, open, onClose, isLoading,
  selectedRamo, setSelectedRamo,
  selectedRubro, setSelectedRubro,
  modoExpandido, setModoExpandido,
  menuAnchorEl, handleMenuClose,
}) => {
  const navigate  = useNavigate();
  const isMobile  = window.innerWidth <= 768;

  const [ramos,     setRamos]     = useState(null);
  const [rubros,    setRubros]    = useState(null);
  const [subrubros, setSubrubros] = useState(null);

  useEffect(() => {
    if (open) {
      getRamos().then(setRamos).catch(console.error);
      getRubros().then(setRubros).catch(console.error);
      getSubrubros().then(setSubrubros).catch(console.error);
    }
  }, [open]);

  const listaRamos     = (Array.isArray(ramos)     ? ramos     : []).sort((a, b) => a.descripcion_ramo.localeCompare(b.descripcion_ramo));
  const listaRubros    = (Array.isArray(rubros)    ? rubros    : []).sort((a, b) => a.descripcion_rubro.localeCompare(b.descripcion_rubro));
  const listaSubrubros = (Array.isArray(subrubros) ? subrubros : []).sort((a, b) => a.descripcion_subrubro.localeCompare(b.descripcion_subrubro));

  const resetSelection = () => { setSelectedRamo(null); setSelectedRubro(null); setModoExpandido(false); };

  // ── Handlers de navegación (usan nuevos params) ───────────────────────────
  const handleVerTodos = () => {
    navigate('/category');
    onClose(); resetSelection();
  };

  const handleRamoClick = (ramo) => {
    navigate(buildUrl(ramo.id));
    handleMenuClose(); resetSelection();
  };

  const handleRubroClick = (rubro) => {
    navigate(buildUrl(selectedRamo.id, rubro.id));
    handleMenuClose(); resetSelection();
  };

const handleSubrubroClick = (sub, rubroId = null) => {
  const rId = rubroId ?? selectedRubro?.id;
  if (!selectedRamo?.id || !rId) return;
  navigate(buildUrl(selectedRamo.id, rId, sub.id));
  handleMenuClose(); resetSelection();
};

  const handleViewOnlyRamo  = () => { navigate(buildUrl(selectedRamo.id));               onClose(); resetSelection(); };
  const handleViewOnlyRubro = () => { navigate(buildUrl(selectedRamo.id, selectedRubro.id)); onClose(); resetSelection(); };

  // Mobile: al seleccionar ramo solo expande, no navega
  const handleRamoSelect  = (ramo)  => { setSelectedRamo(ramo);  if (isMobile) setModoExpandido(true); };
  const handleRubroSelect = (rubro) => {
    const tiene = listaSubrubros.some(s => s.rubro_Id === rubro.id);
    if (tiene) setSelectedRubro(rubro);
    else handleRubroClick(rubro);
  };

  /* ══════════════════════════════════════
     MOBILE
  ══════════════════════════════════════ */
  if (isMobile) {
    return (
      <Dialog open={open} onClose={() => { onClose(); resetSelection(); }} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', overflow: 'hidden' } }}>

        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: '1px solid var(--border)' }}>
          <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
            {!selectedRamo ? 'Categorías' : selectedRubro ? selectedRubro.descripcion_rubro : selectedRamo.descripcion_ramo}
          </Typography>
          <IconButton onClick={() => { handleMenuClose(); resetSelection(); }} disabled={isLoading}
            sx={{ width: 32, height: 32, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {isLoading ? <LoadingDisplay isMobile /> : (
          <Box sx={{ maxHeight: "70vh", overflowY: "auto", p: 2.5 }}>

            {/* Ramos */}
            {!selectedRamo && (
              <Grid container spacing={1.5}>
                {listaRamos.map(ramo => (
                  <Grid item xs={6} key={ramo.id}>
                    <CategoryCard label={ramo.descripcion_ramo} onClick={() => handleRamoSelect(ramo)} color="var(--primary)" />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <CategoryCard label="VER TODOS LOS ARTÍCULOS" onClick={handleVerTodos} color="var(--accent)" />
                </Grid>
              </Grid>
            )}

            {/* Rubros */}
            {selectedRamo && !selectedRubro && (<>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1 }}>
                <PillBtn variant="outline" onClick={() => setSelectedRamo(null)} startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}>Volver</PillBtn>
                <PillBtn variant="success" onClick={handleViewOnlyRamo} startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}>Ver este ramo</PillBtn>
              </Box>
              <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-muted)', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Rubros</Typography>
              <Grid container spacing={1.5}>
                {listaRubros.filter(r => r.ramo_Id === selectedRamo.id).map(rubro => (
                  <Grid item xs={6} key={rubro.id}>
                    <CategoryCard label={rubro.descripcion_rubro} onClick={() => handleRubroSelect(rubro)} color="var(--success)" />
                  </Grid>
                ))}
              </Grid>
            </>)}

            {/* Subrubros */}
            {selectedRubro && (<>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1 }}>
                <PillBtn variant="outline" onClick={() => setSelectedRubro(null)} startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}>Volver</PillBtn>
                <PillBtn variant="purple" onClick={handleViewOnlyRubro} startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}>Ver este rubro</PillBtn>
              </Box>
              <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-muted)', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Subrubros</Typography>
              <Grid container spacing={1.5}>
                {listaSubrubros.filter(s => s.rubro_Id === selectedRubro.id).map(sub => (
                  <Grid item xs={6} key={sub.id}>
                    <CategoryCard label={sub.descripcion_subrubro} onClick={() => handleSubrubroClick(sub)} color="var(--purple)" />
                  </Grid>
                ))}
              </Grid>
            </>)}
          </Box>
        )}
      </Dialog>
    );
  }

  /* ══════════════════════════════════════
     DESKTOP
  ══════════════════════════════════════ */
  return (
    <Popover open={open} anchorEl={menuAnchorEl}
      onClose={() => { if (!isLoading) { handleMenuClose(); resetSelection(); } }}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{ sx: { display: "flex", borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden', mt: 0.5 } }}>

      {/* Lista de Ramos */}
      <List sx={{ minWidth: 170, maxHeight: listaRamos.length > 10 ? 380 : "auto", overflowY: listaRamos.length > 10 ? "auto" : "visible", bgcolor: "var(--bg-card)", borderRight: '1px solid var(--border)', py: 1 }}>
        {isLoading ? <LoadingDisplay isMobile={false} /> : (<>
          {listaRamos.map(ramo => (
            <ListItemButton key={ramo.id}
              onMouseEnter={() => setSelectedRamo(ramo)}
              onClick={() => handleRamoClick(ramo)}
              sx={{
                py: 0.75, px: 2, borderRadius: 'var(--radius-sm)', mx: 0.5, mb: 0.25,
                fontFamily: 'var(--font-body)', fontSize: '0.78rem',
                color: selectedRamo?.id === ramo.id ? 'var(--accent)' : 'var(--text-primary)',
                fontWeight: selectedRamo?.id === ramo.id ? 700 : 500,
                backgroundColor: selectedRamo?.id === ramo.id ? 'var(--accent-soft)' : 'transparent',
                transition: 'all 0.15s ease',
                '&:hover': { backgroundColor: 'var(--bg-subtle)', color: 'var(--accent)' },
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                {ramo.descripcion_ramo}
                {selectedRamo?.id === ramo.id && <ChevronRightIcon sx={{ fontSize: 16, color: 'var(--accent)', ml: 1 }} />}
              </Box>
            </ListItemButton>
          ))}
          <Box sx={{ px: 1, pt: 1 }}>
            <PillBtn variant="accent" onClick={handleVerTodos} sx={{ width: '100%', justifyContent: 'center' }} startIcon={<GridViewIcon sx={{ fontSize: 14 }} />}>
              Ver todos
            </PillBtn>
          </Box>
        </>)}
      </List>

      {/* Rubros + Subrubros */}
      {selectedRamo && (
        <Box sx={{ width: 680, display: "flex", flexDirection: "column", maxHeight: 380, bgcolor: 'var(--bg-subtle)' }}>
          <Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
            <Grid container spacing={2}>

              {/* Vista compacta */}
              {!modoExpandido && listaRubros
                .filter(r => r.ramo_Id === selectedRamo.id)
                .slice(0, 8)
                .map(rubro => {
                  const subs    = listaSubrubros.filter(s => s.rubro_Id === rubro.id);
                  const mostrar = subs.slice(0, 5);
                  return (
                    <Grid item xs={3} key={rubro.id}>
                      <Typography onClick={() => handleRubroClick(rubro)} sx={{
                        fontWeight: 700, fontSize: '0.78rem', mb: 0.75,
                        color: 'var(--text-primary)', cursor: 'pointer',
                        fontFamily: 'var(--font-heading)', letterSpacing: '-0.01em',
                        px: 0.75, py: 0.5, borderRadius: 'var(--radius-sm)',
                        transition: 'all 0.2s ease',
                        '&:hover': { backgroundColor: 'var(--success)', color: '#fff' },
                      }}>
                        {rubro.descripcion_rubro}
                      </Typography>
                      {mostrar.map(sub => (  
                        <MenuItem key={sub.id}   onClick={() => handleSubrubroClick(sub, rubro.id)}  sx={{
                          fontSize: '0.72rem', fontFamily: 'var(--font-body)', px: 1, py: 0.4,
                          color: 'var(--text-secondary)', borderRadius: 'var(--radius-sm)', minHeight: 'auto',
                          transition: 'all 0.15s ease',
                          '&:hover': { backgroundColor: 'var(--purple)', color: '#fff', fontWeight: 600 },
                        }}>
                          {sub.descripcion_subrubro}
                        </MenuItem>
                      ))}
                      {subs.length > 5 && (
                        <MenuItem onClick={() => handleRubroClick(rubro)} sx={{ fontSize: '0.7rem', fontFamily: 'var(--font-body)', px: 1, py: 0.4, fontStyle: 'italic', color: 'var(--accent)', minHeight: 'auto', '&:hover': { backgroundColor: 'var(--accent-soft)' } }}>
                          Ver más...
                        </MenuItem>
                      )}
                    </Grid>
                  );
                })}

              {/* Vista expandida — rubros */}
              {modoExpandido && !selectedRubro && (<>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Rubros en</Typography>
                    <Chip label={selectedRamo.descripcion_ramo} size="small" sx={{ fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '0.72rem', backgroundColor: 'var(--accent-soft)', color: 'var(--accent)' }} />
                  </Box>
                </Grid>
                {listaRubros.filter(r => r.ramo_Id === selectedRamo.id).map(rubro => (
                  <Grid item xs={3} key={rubro.id}>
                    <Typography onClick={() => {
                      const tiene = listaSubrubros.some(s => s.rubro_Id === rubro.id);
                      if (tiene) setSelectedRubro(rubro);
                      else handleRubroClick(rubro);
                    }} sx={{
                      fontWeight: 700, fontSize: '0.78rem', fontFamily: 'var(--font-heading)',
                      color: 'var(--text-primary)', cursor: 'pointer',
                      px: 1, py: 0.75, borderRadius: 'var(--radius-sm)',
                      transition: 'all 0.2s ease',
                      '&:hover': { backgroundColor: 'var(--success)', color: '#fff' },
                    }}>
                      {rubro.descripcion_rubro}
                    </Typography>
                  </Grid>
                ))}
              </>)}

              {/* Vista expandida — subrubros */}
              {modoExpandido && selectedRubro && (<>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Subrubros en</Typography>
                    <Chip label={selectedRubro.descripcion_rubro} size="small" sx={{ fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '0.72rem', backgroundColor: 'rgba(124,58,237,0.08)', color: 'var(--purple)' }} />
                  </Box>
                </Grid>
                {listaSubrubros.filter(s => s.rubro_Id === selectedRubro.id).map(sub => (
                  <Grid item xs={3} key={sub.id}>
                    <MenuItem onClick={() => handleSubrubroClick(sub)} sx={{
                      fontSize: '0.75rem', fontFamily: 'var(--font-body)', fontWeight: 500,
                      px: 1, py: 0.5, color: 'var(--text-secondary)',
                      borderRadius: 'var(--radius-sm)', minHeight: 'auto',
                      transition: 'all 0.15s ease',
                      '&:hover': { backgroundColor: 'var(--purple)', color: '#fff', fontWeight: 600 },
                    }}>
                      {sub.descripcion_subrubro}
                    </MenuItem>
                  </Grid>
                ))}
              </>)}
            </Grid>
          </Box>

          {/* Footer */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 2.5, py: 1.5, borderTop: '1px solid var(--border)', backgroundColor: 'var(--bg-card)' }}>
            {!modoExpandido ? (
              <PillBtn variant="accent" onClick={() => setModoExpandido(true)}>Ver más rubros</PillBtn>
            ) : selectedRubro ? (<>
              <PillBtn variant="accent" onClick={() => handleViewOnlyRubro()}>Ver todo del rubro</PillBtn>
              <PillBtn variant="danger" onClick={() => setSelectedRubro(null)} startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}>Volver</PillBtn>
            </>) : (
              <PillBtn variant="danger" onClick={() => { setModoExpandido(false); setSelectedRubro(null); }} startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}>Volver</PillBtn>
            )}
          </Box>
        </Box>
      )}
    </Popover>
  );
};

export default CategoryPopover;